import { IamFeature } from '@/features/iam'
import { ShellFeature } from '@/features/shell'
import { FeatureTogglesFeature } from '@/features/feature-toggles'
import { AccountFeature } from '@/features/account'
import { BillingFeature } from '@/features/billing'
import { DocumentsFeature } from '@/features/docs'
import { JobsFeature } from '@/features/jobs'
import { ActivityFeature } from '@/features/activity'
import { DashboardFeature } from '@/features/dashboard'
import { MessagesFeature } from '@/features/messages'
import { defer, type Observable } from 'rxjs'
import { localStorage, sessionStorage } from '@/infra/storage'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { BookkeepingFeature } from './features/bookkeeping'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import { SoundFeature } from '@/features/sound'
import { TaxProfileFeature } from '@/features/tax-profile'
import { WaitlistFeature } from '@/features/waitlist'
import { SupportFeature } from '@/features/support'
import { TrackingFeature } from '@/features/tracking'

/**
 * Defines a function for navigating via the router.
 */
export type NavigateFn = (to: string, options?: { replace?: boolean }) => void

/**
 * Options for the root store.
 */
export interface RootStoreOptions {
  /**
   * Provides a way to navigate via the router.
   */
  navigate: NavigateFn

  /**
   * Observable stream for page activity. Emits the current one on subscribe.
   */
  pageActivity$: Observable<PageActivityDescriptor>

  /**
   * Observable stream that emits whether the current user is considered active
   * or inactive.
   */
  userActive$: Observable<boolean>
}

/**
 * The root store, which ties together all the other stores.
 */
export default class RootStore {
  readonly iam
  readonly featureToggles
  readonly shell
  readonly sound
  readonly account
  readonly billing
  readonly bookkeeping
  readonly messages
  readonly taxProfile
  readonly jobs
  readonly docs
  readonly activity
  readonly dashboard
  readonly support
  readonly waitlist
  readonly tracking

  constructor(options: RootStoreOptions) {
    this.iam = IamFeature({
      sessionStorage,
      isUserOnline: (id) => this.messages.onlineStatusForUser(id),
      getLifecycleDelegate: () => this.shell.getLifecycleDelegate(),
      getExternalUrlDelegate: () => this.shell.getExternalUrlDelegate(),
      createSnackbarErrorHandler: (message: string) =>
        this.shell.createSnackbarErrorHandler(message),
    })

    this.featureToggles = FeatureTogglesFeature({
      currentUser$: this.iam.currentUser$,
      currentUserData$: this.iam.currentUserData$,
    })

    this.shell = ShellFeature({
      navigate: options.navigate,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      currentWorkspace$: this.iam.currentWorkspace$,
      workspaceTheme$: this.iam.workspaceTheme$,
      sessionOptions$: this.iam.sessionOptions$,
      fetchTermsAcceptance: (workspaceId, signal) =>
        this.account.fetchTermsAcceptance(workspaceId, signal),
    })

    this.sound = SoundFeature({
      storage: localStorage,
    })

    this.account = AccountFeature({
      token$: this.iam.token$,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
      getWorkspace: this.iam.getWorkspace,
      getCurrentUser: this.iam.getCurrentUser,
    })

    this.billing = BillingFeature({
      token$: this.iam.token$,
      getCurrentMember: this.iam.getCurrentMember,
      newSnackbar: this.shell.newSnackbar,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
    })

    this.bookkeeping = BookkeepingFeature({
      token$: this.iam.token$,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
      getCurrentMember: this.iam.getCurrentMember,
      newSnackbar: this.shell.newSnackbar,
    })

    this.messages = MessagesFeature({
      playSound: this.sound.playSound,
      token$: this.iam.token$,
      currentUser$: this.iam.currentUser$,
      getCurrentUser: this.iam.getCurrentUser,
      primeWorkspace: this.iam.primeWorkspace,
      primeUser: this.iam.primeUser,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
      pageActivity$: options.pageActivity$,
      userActive$: options.userActive$,
    })

    this.taxProfile = TaxProfileFeature({
      token$: this.iam.token$,
      getCurrentMember: this.iam.getCurrentMember,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
    })

    this.jobs = JobsFeature({
      token$: this.iam.token$,
      navigate: options.navigate,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
      newSnackbar: this.shell.newSnackbar,
      getAddToCalendarDelegate: this.shell.getAddToCalendarDelegate,
      getExternalUrlDelegate: this.shell.getExternalUrlDelegate,
      getCurrentMember: this.iam.getCurrentMember,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      getUser: this.iam.getUser,
      primeUser: this.iam.primeUser,
      primeWorkspace: this.iam.primeWorkspace,
      sessionOptions$: this.shell.sessionOptions$,
      ensureTermsAcceptance: this.account.ensureTermsAcceptance,
      ensureSection7216Accepted: this.account.ensureSection7216Acceptance,
      pageExitMethod$: this.shell.pageExitMethod$,
      startHandoff: this.shell.startHandoff,
      // Jobs already depends on docs, so we wrap the promise to avoid an immediate
      // dependency.
      documentsListViewModelFactory: ValueRetrievablePromise.defer(
        () => this.docs.createDocumentListViewModel,
      ),
      documentLinkingViewModelFactory: ValueRetrievablePromise.defer(
        () => this.docs.createDocumentLinkingViewModel,
      ),
      createTransactionTableViewModelFactory:
        this.billing.createTransactionsTableViewModel,
      newBillingMethodDialogViewModelFactory:
        this.billing.createNewBillingMethodDialogViewModel,
      createCardBillingMethodEditorViewModel:
        this.billing.createCardBillingMethodEditorViewModel,
      createBillingMethodListViewModel:
        this.billing.createBillingMethodListViewModel,
      createApplePayViewModel: this.billing.createApplePayViewModel,
      checkHasBillingMethod: this.billing.checkHasDefaultBillingMethod,
      listCoupons: this.billing.listCoupons,
      addCoupon: this.billing.addCoupon,
      fetchCoupon: this.billing.fetchCoupon,
      conversationViewModelFactory: this.messages.createConversationViewModel,
      createBookkeepingPlatformSelectionDialogViewModelFactory:
        this.bookkeeping.createBookkeepingPlatformSelectionDialogViewModel,
      incomingMessages$: this.messages.incomingMessages$,
      unreadCount: this.messages.unreadCount,
      pageActivity$: options.pageActivity$,
      userActive$: options.userActive$,
      createPersonalProfileEditorViewModel:
        this.taxProfile.createPersonalProfileEditorViewModel,
      fetchPersonalProfileVersionForJob:
        this.taxProfile.fetchPersonalProfileVersionForJob,
      createSpousalProfileEditorViewModel:
        this.taxProfile.createSpousalProfileEditorViewModel,
      fetchSpousalProfileVersionForJob:
        this.taxProfile.fetchSpousalProfileVersionForJob,
      createDependentProfileEditorViewModel:
        this.taxProfile.createDependentProfileEditorViewModel,
      fetchDependentProfileVersionForJob:
        this.taxProfile.fetchDependentProfileVersionForJob,
      listDependents: this.taxProfile.listDependents,
      promptDeleteDependent: this.taxProfile.promptDeleteDependent,
      createTaxRefundMethodEditorViewModel:
        this.taxProfile.createTaxRefundMethodEditorViewModel,
      fetchPersonalTaxRefundMethodForJob:
        this.taxProfile.fetchPersonalTaxRefundMethodForJob,
      listTaxRefundMethods: this.taxProfile.listTaxRefundMethods,
      promptDeleteTaxRefundMethod: this.taxProfile.promptDeleteTaxRefundMethod,
      createBusinessProfileEditorViewModel:
        this.taxProfile.createBusinessProfileEditorViewModel,
      fetchBusinessProfileForJob: this.taxProfile.fetchBusinessProfileForJob,
      listBusinessProfiles: this.taxProfile.listBusinessProfiles,
      promptDeleteBusinessProfile: this.taxProfile.promptDeleteBusinessProfile,
      fetchBusinessTaxRefundMethodForJob:
        this.taxProfile.fetchBusinessTaxRefundMethodForJob,
    })

    this.docs = DocumentsFeature({
      token$: this.iam.token$,
      fetchUser: this.iam.fetchUser,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      getCurrentMember: this.iam.getCurrentMember,
      getUser: this.iam.getUser,
      primeUser: this.iam.primeUser,
      fetchJobGist: this.jobs.fetchJobGist,
      jobGistRealtime$: this.jobs.jobGistRealtime$,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
      newSnackbar: this.shell.newSnackbar,
      getDocumentDownloadDelegate: this.shell.getDocumentDownloadDelegate,
    })

    this.activity = ActivityFeature({
      token$: this.iam.token$,
      newSnackbar: this.shell.newSnackbar,
      createSnackbarErrorHandler: this.shell.createSnackbarErrorHandler,
      createSnackbarRetryWrapper: this.shell.createSnackbarRetryWrapper,
      getCurrentUser: this.iam.getCurrentUser,
      getWorkspace: this.iam.getWorkspace,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      getCurrentMember: this.iam.getCurrentMember,
      primeUser: this.iam.primeUser,
      primeWorkspace: this.iam.primeWorkspace,
      systemNotifications$: defer(() => this.jobs.systemNotifications$),
      navigate: options.navigate,
    })

    this.dashboard = DashboardFeature({
      token$: this.iam.token$,
      getCurrentMember: this.iam.getCurrentMember,
      listPublishedLegends: this.jobs.listPublishedLegends,
      createNewDraftJob: this.jobs.createNewDraftJob,
      createJobListViewModel: this.jobs.createJobListViewModel,
      newSearchableJob$: this.jobs.newSearchableJob$,
      listSearchableJobs: this.jobs.listSearchableJobs,
      newSnackbar: this.shell.newSnackbar,
      ensureTermsAcceptance: this.account.ensureTermsAcceptance,
      startHandoff: this.shell.startHandoff,
      sessionOptions$: this.shell.sessionOptions$,
    })

    this.waitlist = WaitlistFeature({
      getCurrentMember: this.iam.getCurrentMember,
    })

    this.support = SupportFeature({
      token$: this.iam.token$,
      getCurrentMember$: this.iam.currentMember$,
    })

    this.tracking = TrackingFeature({
      currentMember$: this.iam.currentMember$,
      logout$: this.iam.logout$,
    })
  }
}
